import { GlobalEventsService } from '../services/global-events.service';
import { createInitializerQueueList } from './initializer-manager';
import { SeoService } from '../services/seo.service';
import { EnvironmentProviders, inject, Injector, provideAppInitializer } from '@angular/core';
import { DecoratorService } from '../helpers/decorator-service.service';
import { InstallAppService } from '../services/install-app.service';
import { GoogleTagManagerService } from '../services/google-tag-manager.service';
import { ServiceWorkerService } from '../services/service-worker.service';
import { ZendeskChatService } from '../services/zendesk/zendesk-chat.service';
import { ThemeService } from '../services/theme.service';
import { AnimatedFaviconService } from '../services/animated-favicon.service';
import { LotteryService } from '../services/lottery.service';
import { BankingService } from '../services/banking.service';
import { ArabicService } from '../services/arabic.service';
import { OneSignalService } from '../services/one-signal.service';
import { UserPhoneService } from '../services/user/user-phone.service';
import { UbidexService } from '../services/ubidex.service';
import { SocketSuccessHandlerService } from '../services/socket-success-handler.service';
import { DataPreloaderService } from '../services/data-preloader.service';
import { GameProvidersService } from '../services/game-providers.service';
import { ContentUpdaterService } from '../services/content-updater/content-updater.service';
import { FraudCheckService } from '../services/fraud-check.service';
import { RestrictSignupService } from '../services/restrict-signup.service';


/**
 * The `initApp` function initializes the DecoratorService with the provided Injector.
 */
export function initApp(injector: Injector) {
  DecoratorService.injector = injector;
}

export const provideInitializer = () => {
  return [
    provideAppInitializer(() => {
      const injector = inject(Injector);
      initApp(injector);
      return Promise.resolve();
    }),
    ...parallelInitializer,
  ];
};


const parallelInitializer: EnvironmentProviders[] = createInitializerQueueList([
  {
    token: DataPreloaderService,
    initializerFn: (service: DataPreloaderService) => service.preloadCriticalData(),
  },
  {
    token: RestrictSignupService,
    initializerFn: (service: RestrictSignupService) => service.checkRestrictSignup(),
  },
  {
    token: ThemeService,
    initializerFn: (service: ThemeService) => service.resolveTheme$(),
  },
  {
    token: GlobalEventsService,
    initializerFn: (service: GlobalEventsService) => service.initGlobalEvent(),
  },
  {
    token: SeoService,
    initializerFn: (service: SeoService) => service.updateGlobalHeadLink(),
  },
  {
    token: InstallAppService,
    initializerFn: (service: InstallAppService) => service.resolveShowVariables(),
  },
  {
    token: LotteryService,
    initializerFn: (service: LotteryService) => service.resolveShowLottery(),
  },
  {
    token: BankingService,
    initializerFn: (service: BankingService) => service.getAllPayments(),
  },
  {
    token: ArabicService,
    initializerFn: (service: ArabicService) => service.resolveArabic(),
  },
  {
    token: OneSignalService,
    initializerFn: (service: OneSignalService) => service.handleId(),
  },
  {
    token: AnimatedFaviconService,
    initializerFn: (service: AnimatedFaviconService) => service.init(),
  },
  {
    token: UserPhoneService,
    initializerFn: (service: UserPhoneService) => service.updatePhoneStoreMask(),
  },
  {
    token: ContentUpdaterService,
    initializerFn: (service: ContentUpdaterService) => service.runContentChangeHandler(),
  },
  {
    token: GameProvidersService,
    initializerFn: (service: GameProvidersService) => service.updateProviderListOnLangChange(),
  },
  {
    token: UbidexService,
    initializerFn: (service: UbidexService) => service.initUbidex(),
  },
  {
    token: FraudCheckService,
    initializerFn: (service: FraudCheckService) => service.initFraudCheck(),
  },
  {
    token: SocketSuccessHandlerService,
    initializerFn: (service: SocketSuccessHandlerService) => service.initHandler(),
  },
  {
    token: GoogleTagManagerService,
    initializerFn: (service: GoogleTagManagerService) => service.addGtmToDom(),
  },
  {
    token: ServiceWorkerService,
    initializerFn: (service: ServiceWorkerService) => service.registerServiceWorker(),
  },
  {
    token: ZendeskChatService,
    initializerFn: (service: ZendeskChatService) => service.initChat(),
  },
]);
